import { area } from '@turf/turf';

export class PeatSummaryReport {
  summary: string;
  location: string;
  thinAge: string;
  harvestAge: number;
  carbonModelled: string;
  undiscountedCosts: string;
  costs: Map<string, BaseRow>;
  financialAnalysis: string;
  undiscountedRevenue: string;
  revenues: Map<string, UndiscountedRevenueRow>;
  totalCost: BaseRow;
  totalRevenue: BaseRow;
  discountRate: number;
  irr: number;
  npv: number;
  cashflowChart: string;
  revenueChart: string;
  financialInputs: FinancialInput[];
  disclaimer: string;

  constructor(
    summary: string,
    location: string,
    thinAge: string,
    harvestAge: number,
    carbonModelled: string,
    undiscountedCosts: string,
    costs: Map<string, BaseRow>,
    undiscountedRevenue: string,
    revenues: Map<string, UndiscountedRevenueRow>,
    totalCost: BaseRow,
    totalRevenue: BaseRow,
    financialAnalysis: string,
    discountRate: number,
    irr: number,
    npv: number,
    cashflowChart: string,
    revenueChart: string,
    financialInputs: FinancialInput[],
    disclaimer: string
  ) {
    this.summary = summary;
    this.location = location;
    this.thinAge = thinAge;
    this.harvestAge = harvestAge;
    this.carbonModelled = carbonModelled;
    this.undiscountedCosts = undiscountedCosts;
    this.costs = costs;
    this.undiscountedRevenue = undiscountedRevenue;
    ``;
    this.revenues = revenues;
    this.totalCost = totalCost;
    this.totalRevenue = totalRevenue;
    this.financialAnalysis = financialAnalysis;
    this.discountRate = discountRate;
    this.irr = irr;
    this.npv = npv;
    this.cashflowChart = cashflowChart;
    this.revenueChart = revenueChart;
    this.financialInputs = financialInputs;
    this.disclaimer = disclaimer;
  }

  percentFormatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1, // Forces 2 decimal places (e.g., 12.34%)
    maximumFractionDigits: 1
  });

  get costKeys() {
    return [...this.costs.keys()];
  }

  getTotalTxt(key: string, isCost: boolean = true) {
    return isCost
      ? this.costs.get(key).totalTxt
      : this.revenues.get(key).totalTxt;
  }

  getPerHaTxt(key: string, isCost: boolean = true) {
    return isCost
      ? this.costs.get(key).perHaTxt
      : this.revenues.get(key).perHaTxt;
  }

  getTonnesOrUPHaTxt(key: string) {
    return this.revenues.get(key).tonnesOrUPHaTxt;
  }

  getRevenueName(key: string) {
    return this.revenues.get(key).nameTxt;
  }

  isTotal(key: string) {
    return key.includes('Total');
  }

  get revenueKeys() {
    return [...this.revenues.keys()];
  }

  get discountRateTxt() {
    return this.percentFormatter.format(this.discountRate);
  }

  get irrTxt() {
    return this.percentFormatter.format(this.irr);
  }

  get npvTxt() {
    return `${getCurrencyFormatter().format(this.npv)} at a Discount Rate of ${
      this.discountRateTxt
    }`;
  }
}

export class SiteQualityAndHarvest {
  siteQuality: string;
  area: number | string;
  valueType: 'Ha' | 'years old';

  constructor(
    siteQuality: string,
    area: number | string,
    valueType: 'Ha' | 'years old'
  ) {
    this.siteQuality = siteQuality;
    this.area = area;
    this.valueType = valueType;
  }
}

export class BaseRow {
  total: number;
  perHa: number;

  constructor(total: number, perHa: number) {
    this.total = total;
    this.perHa = perHa;
  }

  get totalTxt() {
    return getCurrencyFormatter().format(this.total);
  }

  get perHaTxt() {
    return getCurrencyFormatter().format(this.perHa);
  }
}

export class UndiscountedRevenueRow extends BaseRow {
  name: string;
  tonnesOrUPHa: number;
  isTotal?: boolean;

  constructor(
    name: string,
    total: number,
    perHa: number,
    tonnesOrUPHa: number,
    isTotal: boolean = false
  ) {
    super(total, perHa);
    this.name = name;
    this.tonnesOrUPHa = tonnesOrUPHa;
    this.isTotal = isTotal;
  }

  get nameTxt() {
    return this.name;
  }

  get tonnesOrUPHaTxt() {
    return this.tonnesOrUPHa.toLocaleString(undefined, {
      maximumFractionDigits: 0
    });
  }
}

export class CtrlSummaryConfig {
  item: string;
  unit: string;

  constructor(item: string, unit: string) {
    this.item = item;
    this.unit = unit;
  }
}

export type FinancialInput = {
  heading: string;
  tableHeadings: InputColumn[];
  summary: TableInputRow;
};

export type TableInputRow = TableInputColumn[];

export type TableInputColumn = InputColumn[];

export class InputColumn {
  heading: string;
  cls: string;

  constructor(heading: string, cls: string) {
    this.heading = heading;
    this.cls = cls;
  }

  static readonly LEFT_CLS: string = 'px-4 py-2 text-left';
  static readonly RIGHT_CLS: string = 'px-4 py-2 text-right';

  static readonly THREE_HEADINGS: InputColumn[] = [
    new InputColumn('Item', InputColumn.LEFT_CLS),
    new InputColumn('Input Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Unit', InputColumn.LEFT_CLS)
  ];

  static readonly THINNING_HEADINGS: InputColumn[] = [
    new InputColumn('Item', InputColumn.LEFT_CLS),
    new InputColumn('Small Sawlog Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Post Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Pulplog Values ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Unit', InputColumn.LEFT_CLS)
  ];

  static readonly HARVEST_HEADINGS: InputColumn[] = [
    new InputColumn('Item', InputColumn.LEFT_CLS),
    new InputColumn('Large Sawlog Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Small Sawlog Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Pulplog Value ($)', InputColumn.RIGHT_CLS),
    new InputColumn('Unit', InputColumn.LEFT_CLS)
  ];
}

export function getCurrencyFormatter() {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol'
  });
}
